import React from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight } from "react-icons/fi";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/delivery.scss";

const shippingSections = [
  ["processing", "Processing & tracking"],
  ["domestic", "Domestic delivery"],
  ["international", "International delivery"],
  ["preorders", "Preorders & split shipments"],
  ["changes", "Address & order changes"],
  ["delays", "Delays, loss & damage"],
];

function Delivery() {
  const freeShipping = formatPrice(siteConfig.commerce.freeShippingThreshold);
  const standardShipping = formatPrice(siteConfig.commerce.standardShipping);

  return (
    <main className="policy-page delivery">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">SUPPORT / {siteConfig.wordmark}</p>
        <h1>SHIPPING &amp;<br />DELIVERY</h1>
        <p className="policy-page__lede">
          From our studio to your setup: processing windows, delivery estimates,
          tracking, duties, and what to do when a shipment needs attention.
        </p>
        <div className="policy-page__meta">
          <span>POLICY UPDATED AUGUST 13, 2026</span>
          <span>ORDER HELP: <Link to="/contact?subject=shipping">CONTACT SUPPORT</Link></span>
        </div>
      </header>

      <section className="policy-page__facts" aria-label="Shipping policy summary">
        <div>
          <span>01</span>
          <strong>1–3 DAYS</strong>
          <p>Typical processing time for in-stock items before carrier pickup.</p>
        </div>
        <div>
          <span>02</span>
          <strong>{standardShipping} STANDARD</strong>
          <p>Standard U.S. shipping; free on eligible orders of {freeShipping}+.</p>
        </div>
        <div>
          <span>03</span>
          <strong>TRACKED</strong>
          <p>A shipment email is sent when the carrier label is created.</p>
        </div>
      </section>

      <div className="policy-page__layout">
        <aside className="policy-page__toc">
          <p>ON THIS PAGE</p>
          <nav aria-label="Shipping policy sections">
            {shippingSections.map(([id, label], index) => (
              <a key={id} href={`#shipping-${id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <div className="policy-page__intro">
            <p>
              Delivery dates shown at checkout are estimates based on destination,
              item availability, and carrier service. Weekends, holidays, limited
              releases, severe weather, and customs review may add time.
            </p>
          </div>

          <section id="shipping-processing" className="policy-page__section">
            <span className="policy-page__section-number">01</span>
            <h2>PROCESSING &amp; TRACKING</h2>
            <p>
              In-stock orders usually leave our fulfillment center within 1–3
              business days after payment and fraud review. High-volume launches
              may have a longer window; when that happens, the estimate shown on
              the product page or at checkout takes priority.
            </p>
            <ul>
              <li>An order confirmation is sent after checkout is completed.</li>
              <li>A shipping confirmation with tracking is sent when a label is created.</li>
              <li>Tracking can take up to 24 hours to show the first carrier scan.</li>
              <li>Orders placed after cutoff, on weekends, or on holidays begin processing the next business day.</li>
            </ul>
            <p>
              “Partially shipped” means one or more items are on the way while the
              rest are still being prepared. Each parcel receives its own tracking
              number.
            </p>
          </section>

          <section id="shipping-domestic" className="policy-page__section">
            <span className="policy-page__section-number">02</span>
            <h2>DOMESTIC DELIVERY</h2>
            <div className="delivery__table-wrap">
              <table className="delivery__table">
                <thead>
                  <tr><th scope="col">METHOD</th><th scope="col">TRANSIT ESTIMATE</th><th scope="col">RATE</th></tr>
                </thead>
                <tbody>
                  <tr><td>Standard</td><td>3–7 business days</td><td>{standardShipping}</td></tr>
                  <tr><td>Standard over {freeShipping}</td><td>3–7 business days</td><td>Free</td></tr>
                  <tr><td>Expedited</td><td>2–3 business days</td><td>Calculated at checkout</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              Transit estimates begin after carrier pickup and do not include
              processing. Some destinations, including remote regions, P.O. boxes,
              military addresses, and territories, may require additional time or
              a different carrier service.
            </p>
          </section>

          <section id="shipping-international" className="policy-page__section">
            <span className="policy-page__section-number">03</span>
            <h2>INTERNATIONAL DELIVERY</h2>
            <p>
              Available countries and carrier services appear during checkout
              after a complete address is entered. International transit commonly
              takes 7–21 business days after pickup, but customs review can extend
              delivery beyond that range.
            </p>
            <div className="policy-page__note">
              <strong>DUTIES &amp; TAXES</strong>
              <p>
                Unless checkout states that duties are included, international
                orders ship duties unpaid. The recipient is responsible for local
                destination tax, brokerage, customs, and collection charges. These fees
                are not part of the product or shipping price and are not refundable
                by us.
              </p>
            </div>
            <p>
              Refusing a parcel does not guarantee it will return safely. Any
              approved refund for a refused shipment may be reduced by outbound
              shipping, return shipping, duties, and carrier fees where permitted
              by law.
            </p>
          </section>

          <section id="shipping-preorders" className="policy-page__section">
            <span className="policy-page__section-number">04</span>
            <h2>PREORDERS &amp; SPLIT SHIPMENTS</h2>
            <p>
              Preorder products ship in the estimated window shown on their product
              page. Production and freight schedules can change; if a meaningful
              delay occurs, we will send an update to the order email.
            </p>
            <p>
              When in-stock and preorder items are purchased together, the order
              may wait until every item is available or ship in separate parcels.
              Checkout messaging and the order confirmation will indicate the
              expected approach where available. Additional shipping will not be
              charged when we choose to split an order.
            </p>
          </section>

          <section id="shipping-changes" className="policy-page__section">
            <span className="policy-page__section-number">05</span>
            <h2>ADDRESS &amp; ORDER CHANGES</h2>
            <p>
              Review the recipient name, apartment or unit, postal code, and email
              address before checkout. Contact support immediately if something is
              wrong. We will try to help, but an order cannot be changed or canceled
              after warehouse processing or carrier pickup begins.
            </p>
            <p>
              Carrier address changes may be unavailable or may create an extra
              charge. We are not responsible for delay or loss caused by an
              incomplete address entered at checkout. Packages returned to sender
              are handled under the eligibility and fee rules in our <Link to="/returns">Return Policy</Link>.
            </p>
          </section>

          <section id="shipping-delays" className="policy-page__section">
            <span className="policy-page__section-number">06</span>
            <h2>DELAYS, LOSS &amp; DAMAGE</h2>
            <p>
              If tracking has not updated for seven consecutive days, or the parcel
              is past the carrier estimate, check the delivery location and contact
              the carrier first. Then open a support request with your order number
              and tracking number so we can review available options.
            </p>
            <ul>
              <li><strong>Marked delivered:</strong> check household members, neighbors, mailroom staff, and safe-drop locations; allow one additional business day for an early scan.</li>
              <li><strong>Transit damage:</strong> keep the carton, packing materials, shipping label, and product, then photograph each before contacting us.</li>
              <li><strong>Missing item:</strong> compare the packing slip with each parcel and confirm whether the order shows multiple tracking numbers.</li>
            </ul>
            <p>
              Claims must be submitted promptly because carrier deadlines apply.
              Resolution may be a replacement, repair, carrier investigation, or
              refund depending on the facts, stock, and applicable law.
            </p>
            <Link className="site-button site-button--dark delivery__action" to="/contact?subject=shipping">
              GET SHIPPING HELP <FiArrowUpRight aria-hidden="true" />
            </Link>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <span>RELATED</span>
            <Link to="/returns">RETURNS</Link>
            <Link to="/privacy">PRIVACY</Link>
            <Link to="/terms">TERMS</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
